import React from 'react';
import { Card, CardHeader, CardContent } from './Card';
import { Badge } from './Badge';

interface GanttTask {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  progress?: number;
  status?: string;
}

interface GanttChartProps {
  tasks: GanttTask[];
  projectStartDate: string;
  projectEndDate?: string;
}

export function GanttChart({ tasks, projectStartDate, projectEndDate }: GanttChartProps) {
  if (!tasks || tasks.length === 0) {
    return (
      <Card className="p-12 text-center text-muted-foreground font-semibold bg-background/20 border-dashed">
        No timeline data available.
      </Card>
    );
  }

  // Calculate overall timeline span
  const startDates = tasks.map(t => new Date(t.startDate).getTime());
  const endDates = tasks.map(t => new Date(t.endDate || t.startDate).getTime());
  
  const minDate = new Date(Math.min(...startDates, new Date(projectStartDate).getTime()));
  const maxDate = new Date(Math.max(...endDates, projectEndDate ? new Date(projectEndDate).getTime() : new Date().getTime() + 30 * 24 * 60 * 60 * 1000));
  
  const totalDuration = maxDate.getTime() - minDate.getTime();
  const totalDays = Math.ceil(totalDuration / (1000 * 60 * 60 * 24)) + 1;

  const getLeftPercentage = (dateStr: string) => {
    const date = new Date(dateStr).getTime();
    return Math.max(0, ((date - minDate.getTime()) / totalDuration) * 100);
  };

  const getWidthPercentage = (startStr: string, endStr: string) => {
    const start = new Date(startStr).getTime();
    const end = new Date(endStr || startStr).getTime();
    return Math.max(2, ((end - start) / totalDuration) * 100);
  };

  return (
    <Card className="overflow-x-auto">
      <CardHeader className="bg-card/30 px-6 py-4 flex justify-between items-center">
        <h2 className="text-foreground font-display text-section-title">Project Timeline (Gantt)</h2>
        <div className="text-xs text-muted-foreground">
          {minDate.toLocaleDateString()} - {maxDate.toLocaleDateString()}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="min-w-[800px] p-6 space-y-4 relative">
          {/* Timeline Grid Background */}
          <div className="absolute inset-0 left-[200px] pointer-events-none opacity-20">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className="absolute top-0 bottom-0 border-l border-border"
                style={{ left: `${i * 10}%` }}
              />
            ))}
          </div>

          {tasks.map(task => (
            <div key={task.id} className="flex items-center gap-4 group relative">
              <div className="w-[180px] shrink-0">
                <p className="text-sm font-semibold text-foreground truncate" title={task.name}>{task.name}</p>
                <p className="text-xs text-muted-foreground">{new Date(task.startDate).toLocaleDateString()} - {task.endDate ? new Date(task.endDate).toLocaleDateString() : 'TBD'}</p>
              </div>
              
              <div className="flex-1 h-12 relative bg-card/20 rounded-md">
                <div
                  className="absolute top-1/2 -translate-y-1/2 h-6 bg-primary rounded-md group-hover:brightness-110 transition-all flex items-center px-2 cursor-pointer"
                  style={{
                    left: `${getLeftPercentage(task.startDate)}%`,
                    width: `${getWidthPercentage(task.startDate, task.endDate)}%`,
                  }}
                  title={`${task.name}: ${task.progress || 0}% complete`}
                >
                  <div 
                    className="absolute top-0 bottom-0 left-0 bg-white/20 rounded-l-md" 
                    style={{ width: `${task.progress || 0}%` }} 
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
